import React, { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import TimeEventLoading from '../Timevents/TimeEventLoadingSimple';

const PDFViewer = lazy(() => import('./PDFViewer'));
const ImageViewer = lazy(() => import('./ImageViewer'));
const OtherViewer = lazy(() => import('./OtherViewer'));
const AudioViewer = lazy(() => import('./AudioViewer'));
const ExcelViewer = lazy(() => import('./ExcelViewer'));
const HTMLViewer = lazy(() => import('./HTMLViewer'));
const TextViewer = lazy(() => import('./TextViewer'));
const VideoViewer = lazy(() => import('./VideoViewer'));
const EmailViewer = lazy(() => import('./EmailViewer'));
const ZipViewer = lazy(() => import('./ZipViewer'));
const WordViewer = lazy(() => import('./WordViewer'));
const PowerPointViewer = lazy(() => import('./PowerPointViewer'));
const CSVViewer = lazy(() => import('./CSVViewer'));
const JSONViewer = lazy(() => import('./JSONViewer'));

const VIEWER_COMPONENTS = {
  pdf: PDFViewer,
  image: ImageViewer,
  other: OtherViewer,
  audio: AudioViewer,
  excel: ExcelViewer,
  html: HTMLViewer,
  text: TextViewer,
  video: VideoViewer,
  email: EmailViewer,
  zip: ZipViewer,
  word: WordViewer,
  powerpoint: PowerPointViewer,
  csv: CSVViewer,
  json: JSONViewer,
};

const FileViewerContext = createContext({
  openFileViewer: () => {},
  closeFileViewer: () => {},
});

export const useFileViewer = () => useContext(FileViewerContext);

const GlobalFileViewer = React.memo(({ viewer, onClose, isDockedLayout }) => {
  const parentTheme = useTheme();
  
  const viewerTheme = useMemo(() => {
    if (!isDockedLayout) return parentTheme;
    return createTheme(parentTheme, {
      components: {
        MuiDialog: {
          styleOverrides: {
            root: {
              pointerEvents: 'none',
              '& .MuiBackdrop-root': {
                display: 'none !important',
              }
            },
            container: {
              alignItems: 'stretch',
              justifyContent: 'flex-start',
            },
            paper: {
              pointerEvents: 'auto',
              position: 'fixed !important',
              top: '16px !important',
              left: '16px !important',
              bottom: '16px !important',
              margin: '0 !important',
              width: 'min(42vw, 680px) !important',
              maxWidth: '42vw !important',
              maxHeight: 'calc(100vh - 32px) !important',
              height: 'calc(100vh - 32px) !important',
              borderRadius: '16px !important',
              boxShadow: '0 24px 72px rgba(15, 23, 42, 0.28), 0 8px 24px rgba(87, 87, 176, 0.18) !important',
            }
          }
        }
      }
    });
  }, [parentTheme, isDockedLayout]);

  if (!viewer?.type) {
    return null;
  }

  const ViewerComponent = VIEWER_COMPONENTS[viewer.type];
  if (!ViewerComponent) {
    return null;
  }

  return (
    <ThemeProvider theme={viewerTheme}>
      <Suspense fallback={<TimeEventLoading />}>
        <ViewerComponent
          open
          onClose={onClose}
          filename={viewer.filename || ''}
          eventId={viewer.eventId ?? null}
          filePath={viewer.filePath || ''}
          timeevent={viewer.timeevent || null}
        />
      </Suspense>
    </ThemeProvider>
  );
});

const FileViewerProvider = ({ children }) => {
  const [activeViewer, setActiveViewer] = useState(null);
  const theme = useTheme();
  const isDockedLayout = useMediaQuery(theme.breakpoints.up('lg'));

  const closeFileViewer = useCallback(() => {
    setActiveViewer(null);
  }, []);

  const openFileViewer = useCallback((viewer) => {
    setActiveViewer(viewer);
  }, []);

  const value = useMemo(() => ({
    openFileViewer,
    closeFileViewer,
  }), [openFileViewer, closeFileViewer]);

  return (
    <FileViewerContext.Provider value={value}>
      <Box sx={{ position: 'relative' }}>{children}</Box>
      <GlobalFileViewer 
        viewer={activeViewer} 
        onClose={closeFileViewer} 
        isDockedLayout={isDockedLayout} 
      />
    </FileViewerContext.Provider>
  );
};

export default FileViewerProvider;
